import React, { useState, useEffect } from 'react';
import { FiStar, FiChevronLeft, FiChevronRight, FiHeart } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const Testimonials = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const testimonials = [
    {
      name: "Priya & Rajesh Mehta",
      role: "Parents of Aarav (4 years)",
      content: "Aksharam has been a blessing for our family. The caring teachers and safe environment gave us complete peace of mind. Aarav has grown so much in confidence and creativity. The robotics program especially caught his interest!",
      rating: 5,
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face",
      childAge: "Enrolled for 2 years"
    },
    {
      name: "Kavitha & Suresh Kumar",
      role: "Parents of Ananya (3 years)",
      content: "We were nervous about our daughter's first school experience, but the teachers at Aksharam made the transition so smooth. The individual attention and play-based learning approach is exactly what we wanted for Ananya.",
      rating: 5,
      image: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=100&h=100&fit=crop&crop=face",
      childAge: "Enrolled for 1 year"
    },
    {
      name: "Deepika & Arjun Reddy",
      role: "Parents of Ishaan (5 years)",
      content: "The holistic development approach at Aksharam is outstanding. Ishaan not only learned academics but also developed social skills, creativity, and problem-solving abilities. He's now confident and ready for primary school!",
      rating: 5,
      image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=100&h=100&fit=crop&crop=face",
      childAge: "Enrolled for 3 years"
    },
    {
      name: "Sunita & Vikram Sharma",
      role: "Parents of Rhea (4 years)",
      content: "As working parents, we needed a place where our daughter would be safe, happy, and learning. Aksharam exceeded all our expectations. The regular updates and events keep us connected with Rhea's progress.",
      rating: 5,
      image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=100&h=100&fit=crop&crop=face",
      childAge: "Enrolled for 2 years"
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % testimonials.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  useEffect(() => {
    const interval = setInterval(nextSlide, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16" data-aos="fade-up">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            What Parents{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600">
              Say About Us
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Real experiences from families who trust us with their most precious gift – their children.
          </p>
        </div>

        {/* Testimonials Slider */}
        <div className="relative max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-3xl p-8 md:p-12 shadow-xl" data-aos="fade-up">
            <div className="grid md:grid-cols-3 gap-8 items-center">
              {/* Parent Image & Info */}
              <div className="text-center md:text-left">
                <div className="w-24 h-24 mx-auto md:mx-0 mb-4 rounded-full overflow-hidden ring-4 ring-emerald-200">
                  <img
                    src={testimonials[currentSlide].image}
                    alt={`${testimonials[currentSlide].name} - Parent testimonial`}
                    className="w-full h-full object-cover"
                  />
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">
                  {testimonials[currentSlide].name}
                </h4>
                <p className="text-emerald-600 font-medium mb-2">
                  {testimonials[currentSlide].role}
                </p>
                <p className="text-gray-500 text-sm">
                  {testimonials[currentSlide].childAge}
                </p>
                
                {/* Heart Icon */}
                <div className="flex justify-center md:justify-start mt-4">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                    <SafeIcon icon={FiHeart} className="w-4 h-4 text-red-500" />
                  </div>
                </div>
              </div>

              {/* Testimonial Content */}
              <div className="md:col-span-2">
                {/* Stars */}
                <div className="flex justify-center md:justify-start items-center space-x-1 mb-6">
                  {[...Array(testimonials[currentSlide].rating)].map((_, i) => (
                    <SafeIcon key={i} icon={FiStar} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>

                {/* Quote */}
                <blockquote className="text-lg md:text-xl text-gray-700 leading-relaxed italic mb-6">
                  "{testimonials[currentSlide].content}"
                </blockquote>

                {/* Trust Badge */}
                <div className="flex items-center justify-center md:justify-start space-x-2 text-emerald-600">
                  <SafeIcon icon={FiHeart} className="w-4 h-4" />
                  <span className="text-sm font-medium">Verified Parent Review</span>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Buttons */}
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-emerald-50 transition-colors duration-200"
            aria-label="Previous testimonial"
          >
            <SafeIcon icon={FiChevronLeft} className="w-6 h-6 text-emerald-600" />
          </button>

          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-emerald-50 transition-colors duration-200"
            aria-label="Next testimonial"
          >
            <SafeIcon icon={FiChevronRight} className="w-6 h-6 text-emerald-600" />
          </button>
        </div>

        {/* Slide Indicators */}
        <div className="flex justify-center space-x-3 mt-8">
          {testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-colors duration-200 ${
                index === currentSlide ? 'bg-emerald-600' : 'bg-gray-300'
              }`}
              aria-label={`Go to testimonial ${index + 1}`}
            />
          ))}
        </div>

        {/* Bottom Stats */}
        <div className="grid sm:grid-cols-3 gap-8 mt-16 max-w-4xl mx-auto" data-aos="fade-up" data-aos-delay="300">
          <div className="text-center">
            <div className="text-3xl font-bold text-emerald-600 mb-2">98%</div>
            <div className="text-gray-600">Parent Satisfaction Rate</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-emerald-600 mb-2">4.9★</div>
            <div className="text-gray-600">Average Parent Rating</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-emerald-600 mb-2">85%</div>
            <div className="text-gray-600">Families Recommend Us</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;